import { useGroupLifeTargetLivesPage } from '@/features/proposals/hooks/group-life/use-proposal-lives-page.hook';
import { useCnaeLabel } from '@/features/quotes/hooks/group-life/use-cnae-search.hook';
import type {
  GroupLifeFrontPlan,
  GroupLifeQuoteDetails,
} from '@/services/group-life';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { Text } from '@noorden-insurtech/alba-design-system';
import { Building, Building2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type { PmeLifeRow } from '../../proposal.mock';
import { PmeLivesTableSection } from './lives-table-section.component';

const STIPULATOR_TARGET = '__STIPULATOR__';

/** Um estipulante (master ou subestipulante) e suas vidas. */
type LivesGroup = {
  targetId: string;
  title: string;
  legalName: string;
  cnaeCode: string;
  documentLabel: string;
  documentValue: string;
  /** Nome do arquivo exportado. */
  exportLabel?: string;
  isStipulator: boolean;
};

/** Estipulante master primeiro, depois os subestipulantes — a ordem da cotação. */
function buildLivesGroups(details?: GroupLifeQuoteDetails): LivesGroup[] {
  const stipulator = details?.stipulator;
  return [
    {
      targetId: STIPULATOR_TARGET,
      title: 'Estipulante',
      legalName: stipulator?.responsibleName || 'Estipulante',
      cnaeCode: stipulator?.cnaeCode ?? '',
      documentLabel: stipulator?.isCaepf ? 'CAEPF' : 'CNPJ',
      documentValue: stipulator?.registerNumber ?? '',
      exportLabel:
        stipulator?.tradeName || stipulator?.responsibleName || undefined,
      isStipulator: true,
    },
    ...(details?.subStipulators ?? []).map((sub, index) => ({
      targetId: sub.id,
      title: `Subestipulante ${index + 1}`,
      legalName: sub.legalName,
      cnaeCode: sub.cnaeCode ?? '',
      documentLabel: sub.isCaepf ? 'CAEPF' : 'CNPJ',
      documentValue: sub.cnpj,
      exportLabel: sub.legalName,
      isStipulator: false,
    })),
  ];
}

type TableOptions = {
  policyId?: string;
  plan?: GroupLifeFrontPlan;
  multiplySalary?: boolean;
  showImport?: boolean;
  showIncompleteStatus?: boolean;
  showAddMember?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  onLivesChange?: (outcome: { requiresRecalculation: boolean }) => void;
  onImportCompleted?: (outcome: { requiresRecalculation: boolean }) => void;
  requireName?: boolean;
};

type Props = TableOptions & {
  details?: GroupLifeQuoteDetails;
  /**
   * Lista COMPLETA de vidas (todos os targets), separada aqui pelo `targetId`
   * de cada vida e paginada localmente. Sem ela, cada estipulante busca a
   * própria página no servidor (`/members/page`).
   */
  lives?: PmeLifeRow[];
  isLoading?: boolean;
};

/**
 * Lista de vidas da PME separada por estipulante — um card para o estipulante
 * master e um para cada subestipulante, como no passo de vidas da cotação.
 */
export function PmeLivesByStipulantSection({
  details,
  lives,
  isLoading = false,
  ...tableOptions
}: Props) {
  const groups = useMemo(() => buildLivesGroups(details), [details]);
  const livesByTarget = useMemo(() => {
    const byTarget = new Map<string, PmeLifeRow[]>();
    for (const life of lives ?? []) {
      const targetId = life.targetId ?? STIPULATOR_TARGET;
      const targetLives = byTarget.get(targetId) ?? [];
      targetLives.push(life);
      byTarget.set(targetId, targetLives);
    }
    return byTarget;
  }, [lives]);

  return (
    <div className='flex flex-col gap-4'>
      <Text className='font-montserrat text-base font-normal text-alba-black-500'>
        Lista de Vidas
      </Text>
      {groups.map(group => (
        <LivesGroupCard key={group.targetId} group={group}>
          {lives ? (
            <PmeLivesTableSection
              {...tableOptions}
              lives={livesByTarget.get(group.targetId) ?? []}
              total={livesByTarget.get(group.targetId)?.length ?? 0}
              cpfLookupLives={lives}
              isLoading={isLoading}
              targetId={group.targetId}
              exportTargets={[
                { targetId: group.targetId, label: group.exportLabel },
              ]}
              showTitle={false}
            />
          ) : (
            <ServerPagedLivesTable {...tableOptions} group={group} />
          )}
        </LivesGroupCard>
      ))}
    </div>
  );
}

function ServerPagedLivesTable({
  group,
  pageSize: initialPageSize = 10,
  ...tableOptions
}: TableOptions & { group: LivesGroup }) {
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const page = useGroupLifeTargetLivesPage({
    policyId: tableOptions.policyId,
    targetId: group.targetId,
    pageNo,
    pageSize,
  });

  return (
    <PmeLivesTableSection
      {...tableOptions}
      lives={page.lives}
      total={page.total}
      isLoading={page.isLoading || page.isFetching}
      targetId={group.targetId}
      exportTargets={[{ targetId: group.targetId, label: group.exportLabel }]}
      showTitle={false}
      pageSize={pageSize}
      onPageChange={(nextPage, size) => {
        if (size != null && size !== pageSize) {
          setPageSize(size);
          setPageNo(1);
          return;
        }
        setPageNo(nextPage);
      }}
    />
  );
}

function LivesGroupCard({
  group,
  children,
}: {
  group: LivesGroup;
  children: ReactNode;
}) {
  const { data: cnaeInfo } = useCnaeLabel(group.cnaeCode || undefined);
  const Icon = group.isStipulator ? Building : Building2;

  return (
    <div className='rounded-lg border border-alba-gray-200 bg-white'>
      <div className='flex items-center gap-2 border-b border-alba-gray-200 p-4'>
        <Icon
          className={`h-5 w-5 ${
            group.isStipulator ? 'text-alba-violet-500' : 'text-alba-black-500'
          }`}
        />
        <Text className='text-lg font-semibold text-alba-black-500'>
          {group.title}
        </Text>
      </div>
      <div className='space-y-0.5 p-4 pb-2'>
        <Text className='text-base font-bold text-alba-black-500'>
          {group.legalName}
        </Text>
        {group.cnaeCode && (
          <Text className='text-xs text-alba-gray-500'>
            {group.cnaeCode}
            {cnaeInfo?.description ? ` - ${cnaeInfo.description}` : ''}
          </Text>
        )}
        {group.documentValue && (
          <Text className='text-xs text-alba-gray-500'>
            {group.documentLabel}: {formatRegisterNumber(group.documentValue)}
          </Text>
        )}
      </div>
      <div className='p-4 pt-2'>{children}</div>
    </div>
  );
}
